import { useMemo, useState } from 'react';
import { FileText, LifeBuoy, Loader2, Sparkles } from 'lucide-react';
import type { SupportAttachmentDto, SupportCommentDto } from '@/api/control/missionImpactAPI.schemas';
import { SupportHtmlContent } from '@/components/support/SupportHtmlContent';
import { SupportImageViewer } from '@/components/support/SupportImageViewer';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSupportAttachmentUrls } from '@/hooks/useSupportAttachmentUrls';
import {
  fillSupportTemplate,
  formatBytes,
  formatSupportDate,
  getDateLocale,
  getInitials,
  getSupportPersonLabel,
  isImageAttachment,
  isLongComment,
} from '@/lib/support';
import { cn } from '@/lib/utils';

interface SupportMessageProps {
  orgId: string;
  ticketId: string;
  comment: SupportCommentDto;
  /** Attachments posted with this comment (not inline images). */
  attachments: SupportAttachmentDto[];
  /** IDs of the ticket's attachments that have passed the malware scan. */
  cleanAttachmentIds: Set<string>;
  /** Whether this is the ticket's AI summary, pinned first in the conversation. */
  isAiSummary?: boolean;
}

/**
 * One message in a ticket's conversation. The author is shown by avatar and name: the AI summary
 * with its own icon, support staff with the support icon and role tag, customers with initials.
 * Long replies start collapsed, and screenshots posted with the reply show as thumbnails that open
 * full size.
 */
export function SupportMessage({
  orgId,
  ticketId,
  comment,
  attachments,
  cleanAttachmentIds,
  isAiSummary = false,
}: SupportMessageProps) {
  const { t, language } = useLanguage();
  const locale = getDateLocale(language);
  const [expanded, setExpanded] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const isSupport = comment.author?.type === 'Support';
  const isHtml = comment.contentFormat === 'Html';
  const text = comment.comment ?? '';
  const collapsible = isLongComment(isHtml ? text.replace(/<[^>]+>/g, '') : text);
  const collapsed = collapsible && !expanded;

  const name = isAiSummary ? t.support.person.aiSummary : getSupportPersonLabel(comment.author, t.support.person);
  const images = useMemo(
    () => attachments.filter((a) => isImageAttachment(a) && a.id !== undefined && cleanAttachmentIds.has(a.id)),
    [attachments, cleanAttachmentIds]
  );
  const files = attachments.filter((a) => !images.includes(a));
  const imageIds = useMemo(() => images.map((a) => a.id ?? ''), [images]);
  const imageUrls = useSupportAttachmentUrls(orgId, ticketId, imageIds);

  return (
    <li className="grid grid-cols-[2rem_minmax(0,1fr)] gap-x-3">
      <div
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold',
          isAiSummary && 'border border-dashed border-input bg-card text-foreground',
          !isAiSummary && isSupport && 'background-info-strong text-info-strong',
          !isAiSummary && !isSupport && 'background-neutral-strong text-neutral-strong'
        )}
        aria-hidden
      >
        {isAiSummary ? <Sparkles className="h-4 w-4" /> : isSupport ? <LifeBuoy className="h-4 w-4" /> : getInitials(name)}
      </div>
      <div className="rounded-lg border bg-card px-4 py-3">
        <div className="mb-1 flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold">{name}</span>
          {isSupport && !isAiSummary && (
            <span className="rounded-full border border-info background-info px-2 py-0.5 text-xs font-medium text-info">
              {t.support.person.supportRole}
            </span>
          )}
          <span className="text-xs text-muted-foreground">{formatSupportDate(comment.createdAt, locale)}</span>
        </div>
        <div className={cn(collapsed && 'line-clamp-6')}>
          {isHtml ? (
            <SupportHtmlContent orgId={orgId} ticketId={ticketId} html={text} cleanAttachmentIds={cleanAttachmentIds} />
          ) : (
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{text}</p>
          )}
        </div>
        {collapsible && (
          <button
            type="button"
            className="mt-1 text-sm font-medium underline underline-offset-4"
            aria-expanded={expanded}
            onClick={() => { setExpanded((value) => !value); }}
          >
            {expanded ? t.support.detail.showLess : t.support.detail.showMore}
          </button>
        )}
        {images.length > 0 && (
          <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {images.map((image, index) => (
              <li key={image.id}>
                <button
                  type="button"
                  className="group block w-full text-left"
                  aria-label={fillSupportTemplate(t.support.detail.openImage, { name: image.fileName ?? '' })}
                  onClick={() => { setViewerIndex(index); }}
                >
                  <div className="flex h-32 items-center justify-center overflow-hidden rounded-md border bg-muted group-hover:border-ring">
                    {image.id && imageUrls[image.id] ? (
                      <img src={imageUrls[image.id]} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
                    )}
                  </div>
                  <div className="mt-1 flex justify-between gap-2 text-xs">
                    <span className="truncate font-medium">{image.fileName}</span>
                    <span className="shrink-0 text-muted-foreground">{formatBytes(image.fileSize)}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
        {files.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-2">
            {files.map((file) => (
              <li key={file.id} className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
                <FileText className="h-3 w-3 text-muted-foreground" aria-hidden />
                <span className="max-w-48 truncate font-medium">{file.fileName}</span>
                <span className="text-muted-foreground">{formatBytes(file.fileSize)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <SupportImageViewer images={images} urls={imageUrls} index={viewerIndex} onIndexChange={setViewerIndex} />
    </li>
  );
}
